/**
 * Session logout reason transport.
 *
 * We need to surface an informational toast ("You have been logged out due
 * to inactivity." / "Your session has expired. Please log in again.") on the
 * post-logout landing page. `authService.logout()` clears `localStorage` and
 * triggers a full-page redirect to Cognito Hosted UI (which then redirects
 * back to the app root), so in-memory React state does not survive.
 *
 * `sessionStorage` is per-tab and is preserved across the Cognito redirect
 * within the same tab, and is not affected by our `localStorage.clear()` call,
 * so it is the right place to ferry the reason across the boundary.
 */

export const SESSION_LOGOUT_REASON_KEY = "evoly_logout_reason";

/**
 * Reasons a post-auth informational toast may need to be shown on a public
 * page:
 *
 * - `inactivity`        — user was logged out by the idle-timeout warning
 *                         modal (auto-logout or explicit "Log Out Now"), or
 *                         by the org-selection inactivity timer.
 * - `absolute`          — server invalidated the session (401/403 with a
 *                         session error code); typical absolute-timeout path.
 * - `org_access_changed` — organization membership changed while an existing
 *                         session was active and user must re-authenticate.
 * - `unauthenticated`   — user attempted to open a protected URL directly
 *                         without a valid session and `ProtectedRoute`
 *                         bounced them back to the public landing.
 */
export type SessionLogoutReason =
  | "inactivity"
  | "absolute"
  | "org_access_changed"
  | "unauthenticated";

export function setSessionLogoutReason(reason: SessionLogoutReason): void {
  try {
    const current = sessionStorage.getItem(SESSION_LOGOUT_REASON_KEY);

    // Keep the first meaningful reason for this redirect flow.
    // This prevents a later generic "unauthenticated" redirect from overwriting
    // a more specific cause like "org_access_changed".
    if (current === "org_access_changed" || current === "inactivity") {
      return;
    }

    if (current === "absolute" && reason === "unauthenticated") {
      return;
    }

    sessionStorage.setItem(SESSION_LOGOUT_REASON_KEY, reason);
  } catch {
    // sessionStorage may be unavailable (private browsing, quota); fail silently.
  }
}

/**
 * Read and remove the stored logout reason. Safe to call on every app mount —
 * returns `null` when nothing is stored. The value is cleared on read so a
 * subsequent manual refresh of the landing page does not re-show the toast.
 */
export function consumeSessionLogoutReason(): SessionLogoutReason | null {
  try {
    const value = sessionStorage.getItem(SESSION_LOGOUT_REASON_KEY);
    if (value !== null) {
      sessionStorage.removeItem(SESSION_LOGOUT_REASON_KEY);
    }
    return value === "inactivity" ||
      value === "absolute" ||
      value === "org_access_changed" ||
      value === "unauthenticated"
      ? value
      : null;
  } catch {
    return null;
  }
}
